import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import ExpertResumesLayout from "../../../components/ExpertResumesLayout/ExpertResumesLayout";
import { getJobsRoutes } from "../../../handlers/navigationItems";
import { companiesApi } from "../../../services/companies/companiesApi";
import { Button } from "../../JobsPage/components/Button";
import { Input } from "../../JobsPage/components/Input";
import { CompaniesTable } from "./CompaniesTable";
import { CompanyDetails } from "./CompanyDetails";
import { CompanyForm } from "./CompanyForm";

export function CompaniesDashboard() {
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCompany, setSelectedCompany] = useState(null);
	const [editingCompany, setEditingCompany] = useState(null);
	const [sidePanel, setSidePanel] = useState(""); // "view" | "edit" | "create"
	const [itemsPerPage, setItemsPerPage] = useState(25);

	const queryClient = useQueryClient();

	// Fetch companies
	const {
		data: companiesResponse,
		isLoading,
		error,
	} = useQuery({
		queryKey: ["companies"],
		queryFn: companiesApi.getAll,
	});

	const companies = companiesResponse || [];

	// Filter companies based on search term
	const filteredCompanies = companies.filter(
		(company) =>
			company.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			company.company_industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			company.headquarters?.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// Create company mutation
	const createMutation = useMutation({
		mutationFn: companiesApi.create,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["companies"] });
			setSidePanel(null);
		},
		onError: (error) => {
			toast.error("Failed to create company: " + error);
		},
	});

	// Update company mutation
	const updateMutation = useMutation({
		mutationFn: ({ id, data }) => companiesApi.update(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["companies"] });
			setSidePanel(null);
			setEditingCompany(null);
		},
		onError: (error) => {
			toast.error("Failed to update company: " + error);
		},
	});

	// Delete company mutation
	const deleteMutation = useMutation({
		mutationFn: companiesApi.delete,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["companies"] });
		},
		onError: (error) => {
			toast.error("Failed to delete company: " + error);
		},
	});

	const handleView = (company) => {
		setSelectedCompany(company);
		setSidePanel("view");
	};

	const handleEdit = (company) => {
		setEditingCompany(company);
		setSidePanel("edit");
	};

	const handleDelete = (company) => {
		deleteMutation.mutate(company._id);
	};

	const handleCreate = () => {
		setSidePanel("create");
	};

	const handleFormSubmit = (data) => {
		if (editingCompany) {
			updateMutation.mutate({ id: editingCompany._id, data });
		} else {
			createMutation.mutate(data);
		}
	};

	const handleCancel = () => {
		setSidePanel(null);
		setEditingCompany(null);
		setSelectedCompany(null);
	};

	const handleEditFromView = () => {
		if (selectedCompany) {
			setEditingCompany(selectedCompany);
			setSidePanel("edit");
		}
	};

	if (error) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<h2 className="text-2xl font-bold text-destructive mb-2">Error</h2>
					<p className="text-muted-foreground">Failed to load companies. Please try again later.</p>
				</div>
			</div>
		);
	}

	return (
		<ExpertResumesLayout navItems={getJobsRoutes()} headingTitle="Companies" headingIcon="arrow-left">
			<div className="pl-4 my-resumes-section space-y-6">
				{/* Search and Controls */}
				<div className="flex items-center justify-between space-x-4">
					<div className="flex items-center space-x-2 flex-1">
						<div className="relative flex-1 max-w-sm">
							<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
							<Input
								placeholder="Search companies..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="!pl-8 !rounded-sm"
							/>
						</div>
						<div className="text-sm text-muted-foreground">
							{searchTerm ? (
								<>
									{filteredCompanies.length} result{filteredCompanies.length !== 1 ? "s" : ""} of {companies.length}{" "}
									companies
								</>
							) : (
								<>{companies.length} total companies</>
							)}
						</div>
						<Button onClick={handleCreate} size="sm" className="rounded-sm">
							<Plus className="h-4 w-4 mr-2" />
							Add Company
						</Button>
					</div>

					<div className="flex items-center space-x-2">
						<label className="text-sm text-muted-foreground">Show:</label>
						<select
							value={itemsPerPage}
							onChange={(e) => setItemsPerPage(Number(e.target.value))}
							className="h-9 px-3 rounded-sm border border-input bg-background text-sm"
						>
							<option value={10}>10</option>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
						</select>
						<span className="text-sm text-muted-foreground">per page</span>
					</div>
				</div>

				{/* Table */}
				<CompaniesTable
					companies={filteredCompanies}
					onView={handleView}
					onEdit={handleEdit}
					onDelete={handleDelete}
					isLoading={isLoading}
					itemsPerPage={itemsPerPage}
				/>

				{/* Side Panel - Placeholder until Sheet component is installed */}
				{sidePanel && (
					<div className="fixed inset-y-0 right-0 w-1/2 bg-background border-l shadow-lg z-50 overflow-y-auto">
						<div className="p-6">
							<div className="flex items-center justify-between mb-6">
								<h2 className="text-xl font-semibold">
									{sidePanel === "view" && "Company Details"}
									{sidePanel === "edit" && "Edit Company"}
									{sidePanel === "create" && "Create Company"}
								</h2>
								<Button variant="ghost" size="sm" onClick={handleCancel}>
									✕
								</Button>
							</div>

							{sidePanel === "view" && selectedCompany && (
								<CompanyDetails company={selectedCompany} onEdit={handleEditFromView} />
							)}

							{sidePanel === "edit" && editingCompany && (
								<CompanyForm
									company={editingCompany}
									onSubmit={handleFormSubmit}
									onCancel={handleCancel}
									isSubmitting={updateMutation.isPending}
								/>
							)}

							{sidePanel === "create" && (
								<CompanyForm
									onSubmit={handleFormSubmit}
									onCancel={handleCancel}
									isSubmitting={createMutation.isPending}
								/>
							)}
						</div>
					</div>
				)}

				{/* Backdrop */}
				{sidePanel && <div className="fixed inset-0 bg-black/20 z-40" onClick={handleCancel} />}
			</div>
		</ExpertResumesLayout>
	);
}
